import { autoCapitalizePayload } from "utils/autoCapitalize";

import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Card,
  Icon,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  FormControlLabel,
  Checkbox,
  IconButton,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { pageHeaderStyles } from "utils/pageHeaderStyles";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "../examples/Tables/DataTable";
import CustomPagination from "components/CustomPagination";
import EmptyState from "components/EmptyState";
import { useSnackbar } from "components/GlobalSnackbar";
import { useConfirmDialog } from "components/ConfirmDialog";
import { handleApiError } from "utils/errorHandler";
import TambahPenyewa from "components/STPopup/TambahPenyewa";
import EditPenyewa from "components/STPopup/EditPenyewa";
import PrintDialog from "components/STPopup/PrintDialog";
import PrintableReport from "components/PrintableReport";
import printPenyewaPreview from "components/STPrint/PrintPenyewa";
import PenyewaSearch from "components/STSearch/PenyewaSearch";
import { useMaterialUIController } from "context";
import {
  apiGetAllAkaun,
  apiUpdateAkaun,
  apiDeleteAkaun,
  apiSearchAkaun,
  apiGetAkaunFallback,
  apiGetAkaunForPrint,
} from "api/akaun";
import { apiTambahPenyewa, apiDeletePenyewa, apiDeleteAkaunPenyewa } from "api/penyewa";
import { useAkaunData } from "./data/akaun";

export default function MaklumatPenyewa() {
  const { showSuccess, showError, showInfo } = useSnackbar();
  const { confirmDelete, showConfirm } = useConfirmDialog();
  // Add handleSearchChange for PenyewaSearch
  // (Removed duplicate declaration below)
  const [loading, setLoading] = useState(false);
  // Use only DB column names for search state
  const [search, setSearch] = useState({
    nama: "",
    no_kp: "",
    kod_pelanggan: "",
    no_akaun: "",
    zon: "",
    kategori_kutipan: "",
    bahagian_pemantau: "",
    status_akaun: "",
  });
  const [openTambah, setOpenTambah] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openPrintDialog, setOpenPrintDialog] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const debounceRef = useRef();
  const firstLoadRef = useRef(true);
  const [editData, setEditData] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Fixed to 10 items per page

  // Column visibility state with localStorage persistence
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('penyewaColumnVisibility');
    return saved ? JSON.parse(saved) : {
      nama: true,
      kodpelanggan: true,
      noakaun: true,
      kodharta: true,
      bahagianpemantau: true,
      statusakaun: true,
    };
  });

  // Column menu state
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);

  // Inline editing state
  const [editingCell, setEditingCell] = useState(null); // { rowId, column, value, row }
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Dropdown options for inline editing
  const [kodHartaOptions, setKodHartaOptions] = useState([]);

  const [controller] = useMaterialUIController();
  const { darkMode, whiteSidenav, transparentSidenav } = controller;
  const shouldApplyStyle =
    (!darkMode && !whiteSidenav && !transparentSidenav) ||
    (darkMode && !transparentSidenav && whiteSidenav);

  // Handler declarations
  const handleSearchChange = (e) => {
    setSearch({ ...search, [e.target.name]: e.target.value });
    setCurrentPage(1); // Reset to first page when searching
  };

  // Column visibility handlers
  const handleColumnMenuOpen = (event) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const handleColumnToggle = (columnKey) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey],
    };
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('penyewaColumnVisibility', JSON.stringify(newVisibleColumns));
  };

  // Inline editing handlers
  const handleCellEdit = (rowId, column, currentValue, row) => {
    setEditingCell({ rowId, column, value: currentValue, row });
    setEditValue(currentValue || '');
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleSaveEdit = async () => {
    if (!editingCell) return;

    // Validation
    if (!editValue || (typeof editValue === 'string' && editValue.trim() === '')) {
      showError('Nilai tidak boleh kosong');
      return;
    }

    // Check impact if editing nama
    if (editingCell.column === 'nama') {
      try {
        const { apiCheckNamaImpact } = await import('api/akaun');
        const impactRes = await apiCheckNamaImpact(editingCell.row.id);
        
        if (impactRes.affectedCount > 1) {
          const confirmed = await confirmDelete(
            `Perubahan ini akan mempengaruhi ${impactRes.affectedCount} akaun lain dengan nama yang sama. Adakah anda pasti untuk meneruskan?`,
            'Amaran: Perubahan Nama'
          );
          if (!confirmed) {
            return;
          }
        }
      } catch (error) {
        console.error('Error checking nama impact:', error);
        // Continue with save even if check fails
      }
    }

    // Check if kod_harta is being used by another active account
    if (editingCell.column === 'kod_harta') {
      try {
        const { apiCheckKodHartaUsage } = await import('api/akaun');
        const finalValue = editValue.trim ? editValue.trim() : editValue;
        const usageRes = await apiCheckKodHartaUsage(finalValue, editingCell.row.id);
        
        if (usageRes.isUsed && usageRes.owner.status_akaun === 'Aktif') {
          const ownerInfo = `${usageRes.owner.nama} (${usageRes.owner.status_akaun})`;
          
          // Show dialog to select replacement kod_harta for the previous owner
          const availableKodHarta = kodHartaOptions.filter(kod => 
            kod !== finalValue && // Not the one we're trying to assign
            kod !== editingCell.row.kod_harta // Not the current one
          );
          
          if (availableKodHarta.length === 0) {
            showError('Tiada Kod Harta lain yang tersedia untuk ditukar.');
            return;
          }
          
          // Create a custom dialog with dropdown
          const replacement = await new Promise((resolve) => {
            const dialogDiv = document.createElement('div');
            dialogDiv.innerHTML = `
              <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
                <div style="background: white; border-radius: 12px; max-width: 520px; width: 90%; box-shadow: 0 20px 60px rgba(0,0,0,0.3); overflow: hidden; animation: slideIn 0.3s ease-out;">
                  <div style="background: linear-gradient(135deg, #f57c00 0%, #ff9800 100%); padding: 24px; display: flex; align-items: center; gap: 12px;">
                    <svg style="width: 32px; height: 32px; fill: white;" viewBox="0 0 24 24">
                      <path d="M12,2L2,7V17H4V9.09L12,13.09L22,8.09V7L12,2M4,19V21H20V19H4M6,17H8V19H6V17M10,17H12V19H10V17M14,17H16V19H14V17Z"/>
                    </svg>
                    <h3 style="margin: 0; color: white; font-size: 20px; font-weight: 600; letter-spacing: 0.3px;">Penukaran Kod Harta</h3>
                  </div>
                  <div style="padding: 28px 32px;">
                    <div style="background: #fff3e0; border-left: 4px solid #f57c00; padding: 16px; border-radius: 6px; margin-bottom: 24px;">
                      <p style="margin: 0 0 12px 0; font-size: 15px; color: #5d4037; line-height: 1.6;">
                        Kod Harta <strong style="color: #e65100;">${finalValue}</strong> sedang digunakan oleh <strong style="color: #e65100;">${ownerInfo}</strong>.
                      </p>
                      <p style="margin: 0; font-size: 14px; color: #6d4c41;">
                        Sila pilih Kod Harta pengganti untuk akaun <strong>${usageRes.owner.nama}</strong>:
                      </p>
                    </div>
                    <select id="replacementKodHarta" style="width: 100%; padding: 12px 16px; font-size: 15px; border: 2px solid #e0e0e0; border-radius: 8px; background: white; color: #424242; transition: all 0.2s; outline: none; cursor: pointer; font-family: inherit;">
                      <option value="" style="color: #9e9e9e;">-- Pilih Kod Harta Pengganti --</option>
                      ${availableKodHarta.map(kod => `<option value="${kod}" style="padding: 8px;">${kod}</option>`).join('')}
                    </select>
                    <div style="display: flex; gap: 12px; justify-content: flex-end; margin-top: 28px;">
                      <button id="swapConfirm" style="padding: 12px 24px; background: linear-gradient(135deg, #f57c00 0%, #ff9800 100%); color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 14px; transition: all 0.2s; box-shadow: 0 2px 8px rgba(245,124,0,0.3);">
                        ✓ Tukar Sekarang
                      </button>
                      <button id="swapCancel" style="padding: 12px 24px; background: #f5f5f5; color: #616161; border: none; border-radius: 8px; cursor: pointer; font-weight: 500; font-size: 14px; transition: all 0.2s;">
                        Batal
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <style>
                @keyframes slideIn {
                  from {
                    transform: translateY(-20px);
                    opacity: 0;
                  }
                  to {
                    transform: translateY(0);
                    opacity: 1;
                  }
                }
                #replacementKodHarta:focus {
                  border-color: #f57c00 !important;
                  box-shadow: 0 0 0 3px rgba(245,124,0,0.1) !important;
                }
                #replacementKodHarta:hover {
                  border-color: #ff9800 !important;
                }
                #swapConfirm:hover {
                  transform: translateY(-1px);
                  box-shadow: 0 4px 12px rgba(245,124,0,0.4) !important;
                }
                #swapCancel:hover {
                  background: #e0e0e0 !important;
                }
              </style>
            `;
            document.body.appendChild(dialogDiv);
            
            document.getElementById('swapConfirm').onclick = () => {
              const selected = document.getElementById('replacementKodHarta').value;
              if (!selected) {
                document.getElementById('replacementKodHarta').style.borderColor = '#f44336';
                document.getElementById('replacementKodHarta').focus();
                return;
              }
              document.body.removeChild(dialogDiv);
              resolve(selected);
            };
            
            document.getElementById('swapCancel').onclick = () => {
              document.body.removeChild(dialogDiv);
              resolve(null);
            };
          });
          
          if (!replacement) {
            return; // User cancelled
          }
          
          // Perform the swap with replacement
          setSaving(true);
          try {
            const { apiInlineEditAkaun } = await import('api/akaun');
            await apiInlineEditAkaun(editingCell.row.id, 'kod_harta', finalValue, true, replacement);
            
            showSuccess(`Kod Harta berjaya ditukar. ${usageRes.owner.nama} kini menggunakan ${replacement}.`);
            setEditingCell(null);
            setEditValue('');
            setRefreshTrigger(prev => prev + 1);
          } catch (error) {
            handleApiError(error, showError);
          } finally {
            setSaving(false);
          }
          return;
        }
      } catch (error) {
        console.error('Error checking kod_harta usage:', error);
        // Continue with save even if check fails
      }
    }

    setSaving(true);
    try {
      const fieldMap = {
        nama: 'nama',
        kod_pelanggan: 'kod_pelanggan',
        kod_harta: 'kod_harta',
        status_akaun: 'status_akaun',
      };

      const fieldName = fieldMap[editingCell.column];
      const finalValue = editValue.trim ? editValue.trim() : editValue;
      
      // Use new inline-edit API
      const { apiInlineEditAkaun } = await import('api/akaun');
      await apiInlineEditAkaun(editingCell.row.id, fieldName, finalValue);
      
      showSuccess('Rekod berjaya dikemaskini');
      setEditingCell(null);
      setEditValue('');
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      handleApiError(error, showError);
    } finally {
      setSaving(false);
    }
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleCancelEdit();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    }
  };
  
  // Fetch kod_harta options for dropdown
  useEffect(() => {
    const fetchKodHartaOptions = async () => {
      try {
        const { apiGetAllHarta } = await import('api/harta');
        const res = await apiGetAllHarta(1, 1000); // Get up to 1000 harta
        const data = res.data || [];
        const kodSet = new Set();
        data.forEach((row) => {
          if (row.kod_harta && row.kod_harta.trim()) kodSet.add(row.kod_harta.trim());
        });
        const kodArray = Array.from(kodSet).sort();
        setKodHartaOptions(kodArray);
      } catch (e) {
        console.error('Error fetching kod harta options:', e);
        setKodHartaOptions([]);
      }
    };
    fetchKodHartaOptions();
  }, []);

  // Debounce search-triggered refreshes to avoid rapid re-fetch loops
  useEffect(() => {
    const allEmpty =
      !search.nama &&
      !search.no_kp &&
      !search.kod_pelanggan &&
      !search.no_akaun &&
      !search.zon &&
      !search.kategori_kutipan &&
      !search.status_akaun;

    // Skip the initial debounced trigger when everything is empty (avoid double fetch on mount)
    if (firstLoadRef.current && allEmpty) {
      firstLoadRef.current = false;
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setRefreshTrigger((prev) => prev + 1);
    }, 450);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // Watch primitive fields only to avoid object identity churn
  }, [
    search.nama,
    search.no_kp,
    search.kod_pelanggan,
    search.no_akaun,
    search.zon,
    search.kategori_kutipan,
    search.status_akaun,
  ]);
  const handleTambahSave = async (data) => {
    try {
      setLoading(true);
      const capData = autoCapitalizePayload(data, ["email", "e_mail"]);
      const result = await apiTambahPenyewa(capData);
      if (result && result.error) {
        let msg = result.message || result.error || "Ralat semasa menyimpan data.";
        if (result.missingFields) {
          msg += "\nMedan diperlukan: " + result.missingFields.join(", ");
        }
        showError(msg);
        return;
      }
      setRefreshTrigger((prev) => prev + 1); // Trigger data refresh
      setOpenTambah(false);
      showSuccess("Penyewa berjaya ditambah");
    } catch (error) {
      handleApiError(error, showError);
    } finally {
      setLoading(false);
    }
  };

  function handleRowSelect(row) {
    if (!row) {
      setSelectedRow(null);
      return;
    }
    setSelectedRow((prev) => (prev && prev.id === row.id ? null : row));
  }

  // Project Snake: No mapping, use DB column names directly
  const handleEditClick = async () => {
    if (!selectedRow) {
      showInfo("Sila pilih rekod untuk ubahsuai");
      return;
    }
    try {
      setLoading(true);
      const response = await fetch(`/api/penyewa/${selectedRow.id}`);
      if (!response.ok) throw new Error("Gagal dapatkan data penuh penyewa");
      const fullRecord = await response.json();
      setEditData(fullRecord);
      setOpenEdit(true);
    } catch (err) {
      showError("Gagal dapatkan data penuh penyewa untuk ubahsuai");
      setOpenEdit(false);
    } finally {
      setLoading(false);
    }
  };

  // Project Snake: Save using only DB column names, no mapping
  const handleEditSave = async (updatedData) => {
    try {
      setLoading(true);
      const { apiUpdatePenyewa } = await import("api/penyewa");
      const capData = autoCapitalizePayload(updatedData, ["email", "e_mail"]);
      const result = await apiUpdatePenyewa(selectedRow.id, capData);
      if (result && result.error) {
        showError(result.message || "Gagal mengemaskini rekod");
        return;
      }
      setRefreshTrigger((prev) => prev + 1); // Trigger data refresh
      setOpenEdit(false);
      setSelectedRow(null);
      showSuccess("Penyewa berjaya dikemaskini");
    } catch (error) {
      handleApiError(error, showError);
    } finally {
      setLoading(false);
    }
  };
  // ...existing code for handleEditSave...

  const handleDelete = async () => {
    if (!selectedRow) {
      showInfo("Sila pilih rekod untuk dipadam");
      return;
    }

    const tenantName = selectedRow.nama || "penyewa ini";
    const confirmed = await confirmDelete(tenantName);
    
    if (!confirmed) return;

    try {
      setLoading(true);
      // Use apiDeleteAkaunPenyewa to delete only akaun by id
      const result = await apiDeleteAkaunPenyewa(selectedRow.id);
      if (result && result.success) {
        setRefreshTrigger((prev) => prev + 1);
        setSelectedRow(null);
        showSuccess("Penyewa berjaya dipadam");
      } else {
        const msg = result && result.message ? result.message : "Gagal memadam penyewa";
        showError(msg);
      }
    } catch (error) {
      handleApiError(error, showError);
    } finally {
      setLoading(false);
    }
  };

  // Print functionality
  const handlePrintClick = () => {
    setOpenPrintDialog(true);
  };

  const handlePrint = async (filters) => {
    try {
      setLoading(true);
      await printPenyewaPreview(filters);
    } catch (error) {
      setSnackbar({ open: true, message: "Gagal menjana laporan cetak.", severity: "error" });
      alert("Gagal menjana laporan untuk dicetak. Sila cuba lagi.");
    } finally {
      setLoading(false);
    }
  };

  const getFilterTitle = (filters) => {
    if (filters.zonharta && filters.statusakaun) {
      return ` (${filters.zonharta.toUpperCase()}/${filters.statusakaun.toUpperCase()})`;
    } else if (filters.zonharta) {
      return ` (${filters.zonharta.toUpperCase()})`;
    } else if (filters.statusakaun) {
      return ` (${filters.statusakaun.toUpperCase()})`;
    }
    return " (SEMUA)";
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setRefreshTrigger((prev) => prev + 1); // Trigger data refresh
  };

  const handleNextPage = () => {
    if (currentPage < pagination.totalPages) {
      setCurrentPage((prev) => prev + 1);
      setRefreshTrigger((prev) => prev + 1); // Trigger data refresh
    }
  };
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      setRefreshTrigger((prev) => prev + 1); // Trigger data refresh
    }
  };

  // Now, after all state and handler declarations, get table data using the custom hook
  const {
    columns,
    rows,
    pagination,
    loading: tableLoading,
  } = useAkaunData({
    onRowSelect: handleRowSelect,
    selectedRow,
    currentPage,
    itemsPerPage,
    search,
    refreshTrigger,
    visibleColumns,
    editingCell,
    editValue,
    onCellEdit: handleCellEdit,
    onSaveEdit: handleSaveEdit,
    onCancelEdit: handleCancelEdit,
    onEditValueChange: setEditValue,
    onEditKeyDown: handleEditKeyDown,
    saving,
    kodHartaOptions,
  });

  // Combine loading states
  const isLoading = loading || tableLoading;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                sx={pageHeaderStyles.profile}
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Senarai Penyewa
                </MDTypography>
              </MDBox>
              <MDBox>
                <PenyewaSearch
                  search={search}
                  handleSearchChange={handleSearchChange}
                  onTambah={() => setOpenTambah(true)}
                  onEdit={handleEditClick}
                  onDelete={handleDelete}
                  onPrint={handlePrintClick}
                  selectedRow={selectedRow}
                  onColumnMenuOpen={handleColumnMenuOpen}
                />
                {/* Column visibility menu */}
                <Menu
                  anchorEl={columnMenuAnchor}
                  open={Boolean(columnMenuAnchor)}
                  onClose={handleColumnMenuClose}
                  PaperProps={{
                    sx: { mt: 1, minWidth: 200 }
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={visibleColumns.nama}
                        onChange={() => handleColumnToggle('nama')}
                        size="small"
                      />
                    }
                    label="Nama"
                    sx={{ px: 2, py: 0.5 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={visibleColumns.kodpelanggan}
                        onChange={() => handleColumnToggle('kodpelanggan')}
                        size="small"
                      />
                    }
                    label="Kod Pelanggan"
                    sx={{ px: 2, py: 0.5 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={visibleColumns.noakaun}
                        onChange={() => handleColumnToggle('noakaun')}
                        size="small"
                      />
                    }
                    label="No Akaun"
                    sx={{ px: 2, py: 0.5 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={visibleColumns.kodharta}
                        onChange={() => handleColumnToggle('kodharta')}
                        size="small"
                      />
                    }
                    label="Kod Harta"
                    sx={{ px: 2, py: 0.5 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={visibleColumns.bahagianpemantau}
                        onChange={() => handleColumnToggle('bahagianpemantau')}
                        size="small"
                      />
                    }
                    label="Bahagian Pemantau"
                    sx={{ px: 2, py: 0.5 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={visibleColumns.statusakaun}
                        onChange={() => handleColumnToggle('statusakaun')}
                        size="small"
                      />
                    }
                    label="Status Akaun"
                    sx={{ px: 2, py: 0.5 }}
                  />
                </Menu>
                
                {isLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                    <CircularProgress />
                  </Box>
                ) : rows.length === 0 ? (
                  <EmptyState
                    title="Tiada Penyewa Dijumpai"
                    description={
                      Object.values(search).some(val => val)
                        ? "Cuba carian lain atau tambah penyewa baharu"
                        : "Tiada data penyewa. Klik butang 'Tambah' untuk menambah penyewa baharu."
                    }
                  />
                ) : (
                  <>
                    <DataTable
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                      canSearch={false}
                    />
                    <CustomPagination
                      currentPage={pagination.currentPage}
                      totalPages={pagination.totalPages}
                      totalItems={pagination.totalItems}
                      itemsPerPage={itemsPerPage}
                      actualItemsOnPage={pagination.actualItemsOnPage}
                      onPageChange={handlePageChange}
                      onNextPage={handleNextPage}
                      onPrevPage={handlePrevPage}
                      variant="contained"
                      color="info"
                    />
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <TambahPenyewa
        open={openTambah}
        onClose={() => setOpenTambah(false)}
        onSave={handleTambahSave}
      />
      <EditPenyewa
        open={openEdit}
        data={editData}
        onClose={() => setOpenEdit(false)}
        onSave={handleEditSave}
      />
      <PrintDialog
        open={openPrintDialog}
        onClose={() => setOpenPrintDialog(false)}
        onPrint={handlePrint}
      />
    </DashboardLayout>
  );
}
